import React from "react";
import { useSelector } from "react-redux";
import { Field, getFormValues } from "redux-form";
import { ReduxControls, Card } from "rhombus-react";
import DynamicInput from "social/DynamicInput";
import { SNAPCHAT_CAROUSEL_CONSTRAINTS } from "widgets/social2/validators/snapchat";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { isRequiredAssetField, nonDynamicFields } from "social/utils";
import SnapchatDisclaimer from "social/SnapchatDisclaimer";

import "./SnapchatCarouselCreativeDetails.css";

const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_BRAND_NAME,
} = SNAPCHAT_CAROUSEL_CONSTRAINTS;

export const SNAPCHAT_CAROUSEL_CREATIVE_DETAILS_FIELDS = {
	name: {
		name: "name",
		label: "Creative Name",
		component: ReduxControls.Input,
		className: "full-width",
		placeholder: "Enter a creative name",
		required: true,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
	},
	brandName: {
		name: "brandName",
		label: "Brand Name",
		component: DynamicInput,
		defaultDynVarName: "brand_name",
		className: "full-width",
		placeholder: "Enter your brand name",
		charMax: MAX_LENGTH_BRAND_NAME,
		tip: {
			direction: "right",
			description: "Brand name must match the paying Advertiser whose products or services are being advertised",
		},
	},
	headline: {
		name: "headline",
		label: "Headline",
		component: DynamicInput,
		defaultDynVarName: "headline",
		className: "full-width",
		placeholder: "Enter a short headline",
		required: true,
		charMax: MAX_LENGTH_HEADLINE,
	},
	disclaimerText: {
		name: "disclaimerEnabled",
		component: SnapchatDisclaimer,
	},
};

const SNAPCHAT_CAROUSEL_CREATIVE_SETTINGS = [
	{
		name: "blockPreloading",
		label: "Block Web View Preloading",
		className: "setting-checkbox",
		component: ReduxControls.Checkbox,
		useValue: true,
		...nonDynamicFields(),
	},
	{
		name: "allowShare",
		label: "Allow Users to Share with Friends",
		className: "setting-checkbox",
		component: ReduxControls.Checkbox,
		useValue: true,
		...nonDynamicFields(),
	},
];

export default function SnapchatCarouselCreativeDetails(props){
	const {
		adType,
		navigationActions,
		libraryId,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
	} = props;
	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[adType] || {};

	const $fields = Object.values(SNAPCHAT_CAROUSEL_CREATIVE_DETAILS_FIELDS).map((fieldProps) => {
		const extraProps = {};

		if (fieldProps.name === "image") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
			extraProps.required = isRequiredAssetField(assetType);
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<Card
			className="carousel-main-content"
			header="Creative Details"
			isCollapsable
			isVisible
		>
			<div className="carousel-modal-content form-container">
				<div className="fields">
					{$fields}
					<div className="full-width e-form__control-wrapper snapchat-carousel__control-wrapper">
						<div className="e-form__control-header">
							<label className="e-form__control-label">Settings</label>
						</div>
						<div className="settings-fields">
							{SNAPCHAT_CAROUSEL_CREATIVE_SETTINGS.map((setting) => {
								return (
									<Field
										key={setting.name}
										{...setting}
										className="custom-checkbox"
										disabled={isViewOnly}
									/>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</Card>
	);
}
